import { useEffect } from "preact/hooks"
import { useLocation } from "wouter-preact"
import { GlobalFilterState, DateKind } from "@/global_filter/index"
import { OrgConfigState } from "@/org_config/state.tsx"
import {
  date_filter_table_contexts,
  posting_date_filter_available,
  deposit_date_filter_available,
} from "@/global_filter/posting_date_filter.ts"

type DateKindOption = { value: DateKind; label: string }

const INVOICE_OPTION: DateKindOption = { value: "invoice", label: "Invoice Date" }
const CHECK_OPTION: DateKindOption = { value: "check", label: "Check Date" }
const POSTING_OPTION: DateKindOption = { value: "posting", label: "Posting Date" }
/** The payment date entered before pushing a check to the accounting system — the day the cash was booked. */
const DEPOSIT_OPTION: DateKindOption = { value: "deposit", label: "Deposit Date" }

/** The segments to offer: invoice and check always, posting and deposit only where they apply. */
export function date_kind_options(showPosting: boolean, showDeposit: boolean): DateKindOption[] {
  const options = [INVOICE_OPTION, CHECK_OPTION]
  if (showPosting) {
    options.push(POSTING_OPTION)
  }
  if (showDeposit) {
    options.push(DEPOSIT_OPTION)
  }
  return options
}

// Keep per-segment width consistent (~93px) so the labels always fit. A too-narrow container
// makes the nowrap labels overflow the pill.
const WIDTH_CLASS_BY_COUNT: Record<number, string> = {
  2: "w-[188px]",
  3: "w-[280px]",
  4: "w-[372px]",
}

interface DateKindSegmentGroupProps {
  options: { value: DateKind; label: string }[]
  dateKind: DateKind
  widthClass: string
  onSelect: (nextKind: DateKind) => void
}

export function DateKindSegmentGroup({ options, dateKind, widthClass, onSelect }: DateKindSegmentGroupProps) {
  const count = options.length
  const selectedIndex = Math.max(
    0,
    options.findIndex(option => option.value === dateKind)
  )

  return (
    <div
      role="tablist"
      aria-label="Date type"
      class={`relative flex h-9 shrink-0 items-center rounded-full bg-slate-200 p-0.5 transition-[width] duration-200 ease-out ${widthClass}`}
    >
      {/* Single sliding pill so the highlight animates between segments instead of snapping. */}
      <div
        aria-hidden="true"
        class="pointer-events-none absolute top-0.5 bottom-0.5 rounded-full bg-white shadow-sm transition-transform duration-200 ease-out"
        style={{
          left: "0.125rem",
          width: `calc((100% - 0.25rem) / ${count})`,
          transform: `translateX(${selectedIndex * 100}%)`,
        }}
      />
      {options.map(option => {
        const selected = dateKind === option.value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onSelect(option.value)}
            class={`relative z-10 flex h-8 flex-1 cursor-pointer items-center justify-center rounded-full px-1 text-xs font-medium whitespace-nowrap transition-colors ${
              selected ? "text-slate-900" : "text-slate-600 hover:text-slate-900"
            }`}
            style={{ cursor: "pointer" }}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export function DateKindToggle() {
  const [location] = useLocation()
  const dateKind = GlobalFilterState.use(s => s.dateKind)
  const orgFieldConfigs = OrgConfigState.use(s => s.orgFieldConfigs)
  const orgFieldConfigsLoaded = OrgConfigState.use(s => s.orgFieldConfigsLoaded)
  const showPosting = posting_date_filter_available(location, orgFieldConfigs)
  const mightHavePosting = date_filter_table_contexts(location).length > 0
  const showDeposit = deposit_date_filter_available(location)

  useEffect(() => {
    if (!orgFieldConfigsLoaded) {
      return
    }
    if (!showPosting && dateKind === "posting") {
      GlobalFilterState.set(s => ({
        ...s,
        dateKind: "check",
      }))
    }
  }, [orgFieldConfigsLoaded, showPosting, dateKind])

  // Deposit date only means something on the accounting lists; fall back to check date elsewhere
  // so the deductions page never silently filters on it.
  useEffect(() => {
    if (!showDeposit && dateKind === "deposit") {
      GlobalFilterState.set(s => ({
        ...s,
        dateKind: "check",
      }))
    }
  }, [showDeposit, dateKind])

  function handleSelect(nextKind: DateKind) {
    if (nextKind === dateKind) {
      return
    }
    GlobalFilterState.set(s => ({
      ...s,
      dateKind: nextKind,
    }))
  }

  // Before org configs load on routes that may support posting, show 3 segments so
  // Posting stays clickable and is not reset while configs are in flight.
  const showThreeSegments = showPosting || (mightHavePosting && !orgFieldConfigsLoaded)
  const options = date_kind_options(showThreeSegments, showDeposit)
  const widthClass = WIDTH_CLASS_BY_COUNT[options.length]

  return (
    <DateKindSegmentGroup
      options={options}
      dateKind={dateKind}
      widthClass={widthClass}
      onSelect={handleSelect}
    />
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("DateKindToggle segment layout", () => {
    it("uses three segments when posting may be available before configs load", () => {
      const mightHavePosting = date_filter_table_contexts("/transactions").length > 0
      const orgFieldConfigsLoaded = false
      const showPosting = false
      const showThreeSegments = showPosting || (mightHavePosting && !orgFieldConfigsLoaded)
      expect(showThreeSegments).toBe(true)
    })

    it("offers deposit date after posting, and only on accounting routes", () => {
      expect(date_kind_options(true, true).map(o => o.value)).toEqual(["invoice", "check", "posting", "deposit"])
      expect(date_kind_options(false, true).map(o => o.value)).toEqual(["invoice", "check", "deposit"])
      expect(date_kind_options(false, false).map(o => o.value)).toEqual(["invoice", "check"])
      expect(WIDTH_CLASS_BY_COUNT[date_kind_options(true, true).length]).toBe("w-[372px]")
    })

    it("uses two segments when posting is unavailable after configs load", () => {
      const mightHavePosting = date_filter_table_contexts("/transactions").length > 0
      const orgFieldConfigsLoaded = true
      const showPosting = false
      const showThreeSegments = showPosting || (mightHavePosting && !orgFieldConfigsLoaded)
      expect(showThreeSegments).toBe(false)
    })
  })
}
